// 데스크탑 좌측 직원 리스트 (출퇴근 통합 뷰) — Phase 1-2 후속
//   각 직원 카드: 이름 + 회사 + 5색 바 + 카운트 요약
//   클릭 시 상위로 selectedUserId 전달 (우측 캘린더 변경)
//   선택된 직원은 하이라이트
import React, { useState, useEffect } from 'react';
import { ATTENDANCE_FILTER_DEFS } from './utils';
import { btn, mobileEmptyStyle } from './styles';

const PAGE_SIZE = 10;

/**
 * 좌측 직원 리스트
 * Props:
 *   groupedByUser — useAttendanceData.groupedByUser
 *   selectedUserId — 현재 선택된 직원 id
 *   onSelectUser(uid) — 클릭 콜백
 */
export function AttendanceDesktopSummaryList({ groupedByUser, selectedUserId, onSelectUser }) {
  const groups = Object.values(groupedByUser).sort((a, b) =>
    String(a.user.name || '').localeCompare(String(b.user.name || ''), 'ko'),
  );
  // 10명 페이지네이션 — 직원 그룹 변경 시 1페이지로 리셋
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(groups.length / PAGE_SIZE));
  useEffect(() => { setPage(1); }, [groups.length]);
  const safePage = Math.min(page, totalPages);
  const pageGroups = groups.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (groups.length === 0) return <div style={mobileEmptyStyle}>데이터 없음</div>;

  // flex column + 페이지네이션 marginTop:auto → 좌측 영역 height 100% 시 페이지네이션 하단 push (우측 리스트 페이지네이션과 같은 줄)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, height: '100%', minHeight: 0 }}>
      {pageGroups.map((g) => {
        const uid = g.user.id;
        const isSelected = String(selectedUserId) === String(uid);
        const totalDays = g.workDays + g.offDays;
        const stats = ATTENDANCE_FILTER_DEFS.filter((f) => Number(g.counts[f.key] || 0) > 0);
        return (
          <article
            key={`d-${uid}`}
            onClick={() => onSelectUser(uid)}
            style={{
              padding: '8px 10px',
              border: `1px solid ${isSelected ? '#6366f1' : 'var(--border)'}`,
              borderRadius: 10, background: isSelected ? 'rgba(99,102,241,0.08)' : 'var(--bg-card)',
              cursor: 'pointer', display: 'grid', gap: 4,
              transition: 'border-color 0.1s, background 0.1s',
            }}
          >
            {/* 이름 + 회사 + 부서 + 일수 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <strong style={{ fontSize: 13, fontWeight: 700 }}>{g.user.name}</strong>
              {g.user.company_name && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: '#fef9c3', color: '#92400e', border: '1px solid #fde68a' }}>
                  {g.user.company_name}
                </span>
              )}
              {g.user.department && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{g.user.department}</span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
                근무 {g.workDays} · 휴무 {g.offDays}
              </span>
            </div>
            {/* 5색 바 시각화 */}
            {totalDays > 0 && (
              <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: 'var(--bg-subtle)' }}>
                {ATTENDANCE_FILTER_DEFS.map((f) => {
                  const cnt = Number(g.counts[f.key] || 0);
                  if (cnt === 0) return null;
                  return (
                    <div key={f.key} title={`${f.label} ${cnt}일`} style={{ width: `${(cnt / totalDays) * 100}%`, background: f.color, height: '100%' }} />
                  );
                })}
              </div>
            )}
            {/* 카운트 요약 */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 11, color: 'var(--text-sub)' }}>
              {stats.map((f, i) => (
                <React.Fragment key={f.key}>
                  {i > 0 && <span style={{ color: 'var(--border)' }}>·</span>}
                  <span style={{ color: f.color, fontWeight: 600 }}>{f.label} {Number(g.counts[f.key] || 0)}</span>
                </React.Fragment>
              ))}
            </div>
          </article>
        );
      })}
      {/* 페이지네이션 — 10명 단위 (marginTop:auto 로 영역 하단 push) */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, paddingTop: 4, fontSize: 12, marginTop: 'auto' }}>
          <button style={{ ...btn, padding: '2px 10px', fontSize: 12 }} disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>이전</button>
          <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{safePage} / {totalPages}</span>
          <button style={{ ...btn, padding: '2px 10px', fontSize: 12 }} disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>다음</button>
        </div>
      )}
    </div>
  );
}
